import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Box, IconButton, InputBase, Tooltip, Typography } from "@mui/material";
import {
  Search as SearchIcon,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Close as CloseIcon,
} from "@mui/icons-material";

const navIconButtonSx = {
  padding: 0,
  width: "22px",
  height: "22px",
  minWidth: "22px",
  borderRadius: "4px",
  color: "text.secondary",
  "&:hover": { backgroundColor: "action.hover", color: "text.primary" },
  "&.Mui-disabled": { color: "text.disabled" },
};

function SessionDocumentSearchBar({
  query,
  onQueryChange,
  totalHits,
  matchedSectionCount,
  currentMatchIndex,
  onPrev,
  onNext,
  onClear,
}) {
  const inputRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "f") {
        e.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (e.shiftKey) onPrev();
      else onNext();
    } else if (e.key === "Escape") {
      e.preventDefault();
      onClear();
    }
  };

  const hasQuery = query.length > 0;
  const counter =
    hasQuery && matchedSectionCount > 0
      ? `${currentMatchIndex + 1}/${matchedSectionCount}`
      : hasQuery
      ? "0/0"
      : "";

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "6px",
        height: "36px",
        px: "12px",
        borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        backgroundColor: "background.paper",
      }}
    >
      <SearchIcon
        sx={{ fontSize: "16px", color: "text.disabled", flexShrink: 0 }}
      />
      <InputBase
        inputRef={inputRef}
        value={query}
        onChange={(e) => onQueryChange(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Search session data"
        sx={{
          flex: 1,
          fontSize: "13px",
          "& input": { padding: 0 },
        }}
      />
      {hasQuery && (
        <>
          <Typography
            sx={{
              fontSize: "11px",
              color: totalHits > 0 ? "text.secondary" : "text.disabled",
              minWidth: "40px",
              textAlign: "right",
              flexShrink: 0,
            }}
          >
            {counter}
            {totalHits > 0 && matchedSectionCount > 0
              ? ` · ${totalHits} hits`
              : ""}
          </Typography>
          <Tooltip title="Previous (Shift+Enter)">
            <span>
              <IconButton
                size="small"
                disableRipple
                onClick={onPrev}
                disabled={matchedSectionCount === 0}
                sx={navIconButtonSx}
              >
                <KeyboardArrowUp sx={{ fontSize: "16px" }} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Next (Enter)">
            <span>
              <IconButton
                size="small"
                disableRipple
                onClick={onNext}
                disabled={matchedSectionCount === 0}
                sx={navIconButtonSx}
              >
                <KeyboardArrowDown sx={{ fontSize: "16px" }} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Clear (Esc)">
            <IconButton
              size="small"
              disableRipple
              onClick={onClear}
              sx={navIconButtonSx}
            >
              <CloseIcon sx={{ fontSize: "14px" }} />
            </IconButton>
          </Tooltip>
        </>
      )}
    </Box>
  );
}

SessionDocumentSearchBar.propTypes = {
  query: PropTypes.string.isRequired,
  onQueryChange: PropTypes.func.isRequired,
  totalHits: PropTypes.number.isRequired,
  matchedSectionCount: PropTypes.number.isRequired,
  currentMatchIndex: PropTypes.number.isRequired,
  onPrev: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
};

export default SessionDocumentSearchBar;
